// bm-design-system: quantity stepper primitive
//
// −  [ value ]  +   — a whole-number stepper for single-bottle quantities.
// Controlled: the caller owns `value` and decides what `onChange` does
// (e.g. an Inertia router.patch). The stepper only ever moves one at a time;
// the number input can be typed into and commits on blur or Enter.
// SSR-safe: no browser globals.
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

const stepperVariants = cva(
  "inline-flex items-stretch border border-hairline bg-charcoal",
  {
    variants: {
      size: {
        sm: "h-10",
        md: "h-12",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const stepperButtonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center text-ink-body transition-colors hover:bg-charcoal-2 hover:text-accent focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-body",
  {
    variants: {
      size: {
        sm: "w-10",
        md: "w-12",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const stepperInputVariants = cva(
  // .form-control-cellar supplies the charcoal fill, hairline and gold focus;
  // the wrapper draws the outer border so the input drops its own sides.
  "form-control form-control-cellar h-full rounded-none border-y-0 px-1 text-center tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
  {
    variants: {
      size: {
        sm: "w-14",
        md: "w-16",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface QuantityStepperProps
  extends VariantProps<typeof stepperVariants> {
  /** Current whole-number quantity. */
  value: number;
  /** Called with the next whole number (clamped to min/max). */
  onChange: (next: number) => void;
  /** Accessible name for the group and input, e.g. "Bottles of Chenin Blanc 2023". */
  label: string;
  /** Unit read out by the live region; default "bottles" (singular "bottle" at 1). */
  unit?: string;
  min?: number;
  max?: number;
  id?: string;
  /** Form field name, for use inside a plain <form>. */
  name?: string;
  disabled?: boolean;
  className?: string;
}

function toWhole(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number.parseInt(trimmed, 10);
}

const QuantityStepper = React.forwardRef<HTMLDivElement, QuantityStepperProps>(
  (
    {
      value,
      onChange,
      label,
      unit = "bottles",
      min = 0,
      max,
      id,
      name,
      disabled = false,
      size,
      className,
    },
    ref,
  ) => {
    const [draft, setDraft] = React.useState(String(value));

    // Keep the input in step when the value changes from outside
    // (server round-trip, another stepper for the same wine).
    React.useEffect(() => {
      setDraft(String(value));
    }, [value]);

    const clamp = (n: number) => {
      let next = Math.max(min, Math.floor(n));
      if (max !== undefined) next = Math.min(max, next);
      return next;
    };

    const commit = (next: number) => {
      const clamped = clamp(next);
      setDraft(String(clamped));
      if (clamped !== value) onChange(clamped);
    };

    const commitDraft = () => {
      const parsed = toWhole(draft);
      if (parsed === null) {
        setDraft(String(value));
        return;
      }
      commit(parsed);
    };

    const atMin = value <= min;
    const atMax = max !== undefined && value >= max;
    const unitWord = value === 1 && unit.endsWith("s") ? unit.slice(0, -1) : unit;

    return (
      <div
        ref={ref}
        role="group"
        aria-label={label}
        className={cn(stepperVariants({ size }), disabled && "opacity-60", className)}
      >
        <button
          type="button"
          className={stepperButtonVariants({ size })}
          onClick={() => commit(value - 1)}
          disabled={disabled || atMin}
          aria-label={`Remove one (${label})`}
        >
          <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <input
          id={id}
          name={name}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={draft}
          disabled={disabled}
          aria-label={label}
          className={stepperInputVariants({ size })}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitDraft();
            }
          }}
        />
        <button
          type="button"
          className={stepperButtonVariants({ size })}
          onClick={() => commit(value + 1)}
          disabled={disabled || atMax}
          aria-label={`Add one (${label})`}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {`${value} ${unitWord}`}
        </span>
      </div>
    );
  },
);
QuantityStepper.displayName = "QuantityStepper";

export { QuantityStepper, stepperVariants };
